import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, Lock, CheckCircle } from 'lucide-react'
import { authService } from '../services/auth'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorModal from '../components/ErrorModal'

interface ResetPasswordForm {
  password: string
  confirmPassword: string
}

export default function ResetPasswordPage() {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  const token = searchParams.get('token')
  
  const { register, handleSubmit, formState: { errors }, watch } = useForm<ResetPasswordForm>()
  const password = watch('password')

  useEffect(() => {
    if (!token) {
      setError('重置链接无效或已过期')
    }
  }, [token])

  const onSubmit = async (data: ResetPasswordForm) => {
    if (loading || !token) return
    
    setLoading(true)
    setError('')

    try {
      const response = await authService.resetPassword(token, data.password)
      if (response.success) {
        setSuccess(true)
        // 3秒后跳转到登录页面
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      } else {
        setError(response.error || '密码重置失败')
        setShowErrorModal(true)
      }
    } catch (err) {
      setError('密码重置失败，请重试')
      setShowErrorModal(true)
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <Link to="/" className="text-2xl font-bold text-gray-900">
              Sku Studio
            </Link>
          </div>
          <div className="mt-8 bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Lock className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                链接无效
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                重置密码链接无效或已过期，请重新申请密码重置。
              </p>
              <div className="mt-6">
                <Link
                  to="/forgot-password"
                  className="w-full btn-primary py-2 text-center block"
                >
                  重新申请密码重置
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <Link to="/" className="text-2xl font-bold text-gray-900">
              Sku Studio
            </Link>
          </div>
          <div className="mt-8 bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                密码重置成功
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                您的密码已成功重置，3秒后将自动跳转到登录页面。
              </p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="w-full btn-primary py-2 text-center block"
                >
                  立即登录
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <Link to="/" className="text-2xl font-bold text-gray-900">
            Sku Studio
          </Link>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          重置密码
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          请输入您的新密码
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                新密码
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('password', {
                    required: '请输入新密码',
                    minLength: {
                      value: 6,
                      message: '密码至少需要6位字符'
                    }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="input pr-10"
                  placeholder="输入新密码"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                确认密码
              </label>
              <div className="mt-1 relative">
                <input
                  {...register('confirmPassword', {
                    required: '请确认密码',
                    validate: value =>
                      value === password || '两次输入的密码不一致'
                  })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className="input pr-10"
                  placeholder="再次输入新密码"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-2 btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    重置密码
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-gray-600 hover:text-gray-500"
            >
              返回登录
            </Link>
          </div>
        </div>
      </div>

      {/* 错误弹窗 */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="重置失败"
        message={error}
      />
    </div>
  )
}